import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { SasAutocompleteDirective } from '@shared/directives/sas-autocomplete.directive';
import { SasDynamicFormService } from '@shared/sas-dynamic-form/sas-dynamic-form.service';
import { SasLabelValueComponent } from '@shared/sas-label-value/sas-label-value.component';
import { FormSubmittedService } from '@shared/services/form-submitted.service';

@Component({
    selector: 'sas-input-text-field-type',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, FormlyModule, TranslateModule, SasAutocompleteDirective, SasLabelValueComponent],
    providers: [],
    template: `
        @if (isShowingSummaryInfo) {
            <sas-label-value [label]="field.props.label ?? ''"
                             [model]="formControl.value"
                             sasClass="textmode toplabel mb-2">
            </sas-label-value>
        } @else {
            <div class="form-group mb-3">
                <label [for]="field.id"
                       [id]="field.id + 'Label'"
                       class="form-label"
                       [textContent]="field.props.label"
                       translate="{{field.props.label}}">
                </label>
                @if (field.props.required) {
                <span> *</span>
                }
                <div [id]="field.id + 'Help'" [title]="helpText" class="sas-inline-block">
                    @if (helpText.length && !isReadonly) {
                    <i class="bi bi-info-circle-fill" aria-hidden="true" style="font-size: 15px; cursor: help; margin-left: 5px;"></i>
                    }
                </div>
                <input type="text"
                       [name]="field.name"
                       class="form-control"
                       [ngClass]="{'is-invalid': isInvalid}"
                       [id]="field.id"
                       [formControl]="formControl"
                       [placeholder]= "!isReadonly ? (field.props.placeholder! | translate) : ''"
                       maxlength="{{field.props.maxLength}}"
                       sasAutoComplete
                       tabindex="0"
                       required="{{field.props.required}}"
                       [readonly]="isReadonly"
                       pattern="{{field.props['validation'].pattern}}"
                       [attr.aria-invalid]="isInvalid"
                       [attr.aria-labelledby]="field.id + 'Label'"
                       [attr.aria-describedby]="field.id + 'Help'"/>
                <div [id]="field.id + 'Desc'" role="alert" aria-atomic="true" class="invalid-feedback">
                    <small>{{ sasDynamicFormService.getFieldErrorMessage(field) }}</small>
                </div>
            </div>
        }
    `,
    styleUrl: './sas-input-text-field-type.component.scss'
})

export class SasInputTextFieldTypeComponent extends FieldType<FieldTypeConfig> {
    formSubmittedService = inject(FormSubmittedService);
    translate = inject(TranslateService);
    sasDynamicFormService = inject(SasDynamicFormService);

    helpText: string = '';
    isShowingSummaryInfo: boolean = false;

    get isReadonly(): boolean {
        return (this.field.formControl.disabled || this.field.props.readonly) ?? false;
    }

    get isInvalid() {
        return this.field.formControl.invalid && (this.field.formControl.dirty || this.formSubmittedService.isSubmitted())
    }

    ngOnInit() {
        if (this.field.props['helptext']) {
            const helpText = this.translate.instant(this.field.props['helptext']);
            this.helpText = helpText === this.field.props['helptext'] ? '' : helpText;
        }

        this.isShowingSummaryInfo = this.field.props['isShowingSummaryInfo'] ?? false;
    }
}
